import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Heart, ShieldCheck, WifiOff, QrCode, Lock, Phone,
  MessageCircle, CheckCircle, ArrowRight, Search, CalendarCheck, Building2, Stethoscope,
  Smile, Info, UserPlus, Eye
} from 'lucide-react';

const VALUE_PILLARS = [
  {
    icon: CalendarCheck,
    title: 'Book in 30 seconds',
    desc: "No apps to install. Enter your name, pick a slot, get a booking code. Simple, direct, no middlemen.",
    color: 'emerald',
  },
  {
    icon: WifiOff,
    title: 'Works offline after one visit',
    desc: "Your doctor's site is saved on your phone after the first visit. Call and WhatsApp buttons work in zero-network areas — built for real India.",
    color: 'sky',
  },
  {
    icon: QrCode,
    title: 'Save contact with one scan',
    desc: "Scan the QR on your doctor's site — their phone, WhatsApp, and clinic addresses save to your phone. No typing, no mistakes.",
    color: 'violet',
  },
  {
    icon: Stethoscope,
    title: 'Real doctors, real credentials',
    desc: 'NMC/State Medical Council registrations displayed. Qualifications, experience, procedures, fees — all self-published by the doctor. No paid rankings. No commercial manipulation.',
    color: 'amber',
  },
  {
    icon: MessageCircle,
    title: 'Talk before you arrive',
    desc: 'Every booking comes with a short chat (3 messages each side) so you can say "running late" or ask what to bring — and the doctor can respond.',
    color: 'rose',
  },
  {
    icon: ShieldCheck,
    title: 'Your privacy, protected',
    desc: "No data sold, no spam, no sharing your bookings with anyone. Your health decisions are yours alone.",
    color: 'slate',
  },
  {
    icon: Heart,
    title: 'Refer your doctor to friends',
    desc: "Had a great experience? Share your doctor's profile with a tracked referral link. Help others find trustworthy care — your referrals are tracked on your profile.",
    color: 'pink',
  },
  {
    icon: Search,
    title: 'Find specialists near you',
    desc: "Browse doctors by specialty and city — Cardiologists in Mumbai, ENT in Delhi. No ads, no paid placements. Just real doctors ranked by their published expertise.",
    color: 'teal',
  },
];

const HOW_IT_WORKS = [
  { num: '01', icon: Search, title: 'Find your doctor', desc: "Scan their clinic QR, open the link they shared on WhatsApp, or click through Google." },
  { num: '02', icon: CalendarCheck, title: 'Pick a slot, get a code', desc: 'Choose a clinic, a time, and confirm. You get a 6-digit booking code you can show at reception.' },
  { num: '03', icon: Building2, title: 'Show up (or chat if delayed)', desc: 'Arrive at the clinic. Running late? Use the built-in chat. Need to save their contact? Scan the QR.' },
];

const colorMap = {
  emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  sky: 'bg-sky-50 text-sky-600 border-sky-100',
  violet: 'bg-violet-50 text-violet-600 border-violet-100',
  amber: 'bg-amber-50 text-amber-600 border-amber-100',
  rose: 'bg-rose-50 text-rose-600 border-rose-100',
  slate: 'bg-slate-100 text-slate-700 border-slate-200',
};

export default function PatientValueProp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-sky-50/30 to-emerald-50/20" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>For Patients — Book Doctors Directly on DocSite</title>
        <meta name="description" content="Book directly with your doctor. No signup. Works offline. Privacy respected. The simplest way to reach real doctors in India." />
        <meta property="og:title" content="DocSite — Reach Your Doctor. Directly." />
      </Helmet>

      {/* Hero */}
      <section className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-12 md:py-16" data-testid="patients-hero">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-12 items-start">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold mb-6">
              <Heart size={12} /> FOR PATIENTS
            </div>
            <h1 className="tracking-tight font-medium text-slate-900 leading-[1.05]" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2.25rem, 4.6vw, 5.25rem)' }} data-testid="patients-h1">
              Reach Your Doctor.<br />
              <span className="text-sky-600">Directly.</span>
            </h1>
            <p className="text-slate-600 mt-6 leading-relaxed" style={{ fontSize: 'clamp(1rem, 1.05vw, 1.2rem)' }}>
              Install DocSite, link up with your doctor&apos;s digital clinic in one tap. Register once, pick your doctor, <b>scan their QR to auto-save their clinic</b>, and book your 15-minute slot in seconds — every time.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link to="/dr/dr-rajesh-sharma" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-800 transition-colors" style={{ fontSize: 'clamp(0.95rem, 1vw, 1.1rem)' }} data-testid="patients-see-sample">
                See a sample doctor site <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-200 text-slate-700 font-medium hover:border-slate-300 transition-colors" style={{ fontSize: 'clamp(0.95rem, 1vw, 1.1rem)' }} data-testid="patients-contact">
                Contact us
              </Link>
            </div>
          </div>

          {/* Try-the-demo card — fluid scaling, balances the hero column */}
          <aside className="hidden lg:block lg:mt-4" data-testid="patients-hero-demo-card">
            <div className="rounded-3xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 text-white p-7 lg:p-9 shadow-xl shadow-emerald-900/20 relative overflow-hidden" style={{ fontSize: 'clamp(0.95rem, 1.05vw, 1.2rem)' }}>
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" aria-hidden="true" />
              <div className="relative">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 backdrop-blur font-bold uppercase tracking-[0.18em] border border-white/20" style={{ fontSize: '0.65em' }}>
                  <Eye size={11} /> Live demo
                </span>
                <h3 className="mt-5 font-medium leading-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.5em' }}>
                  See what your patient dashboard looks like — without signing up.
                </h3>
                <p className="text-emerald-50/90 mt-4 leading-relaxed" style={{ fontSize: '0.9em' }}>
                  Saved clinics, one-tap booking, the QR card you can show any doctor. All real UI, no login required.
                </p>
                <Link
                  to="/preview/my-clinics"
                  className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-white text-emerald-700 font-bold hover:bg-emerald-50 transition-colors w-full justify-center"
                  style={{ fontSize: '0.95em' }}
                  data-testid="patients-hero-try-demo"
                >
                  Try the demo <ArrowRight size={15} />
                </Link>
                <p className="text-emerald-100/70 mt-4 text-center italic" style={{ fontSize: '0.7em' }}>
                  Forwardable link — drop it in any patient WhatsApp group.
                </p>
              </div>
            </div>
          </aside>
        </div>

        {/* ====== Trust row + JUMP-TO PILLS — color-coded, signals there's much more below the fold ====== */}
        <div className="mt-8 pt-6 border-t border-slate-200 space-y-4">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-slate-600 font-medium" style={{ fontSize: 'clamp(0.95rem, 1.05vw, 1.2rem)' }} data-testid="patients-trust-row">
            <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> Always free for patients</span>
            <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> Saved across devices</span>
            <span className="flex items-center gap-2"><CheckCircle size={20} className="text-emerald-500" /> One-tap booking, every visit</span>
          </div>

          <nav aria-label="Patient page section shortcuts" data-testid="patients-jump-nav">
            <p className="font-bold text-slate-500 uppercase tracking-[0.18em] mb-3 flex items-center gap-1.5" style={{ fontSize: 'clamp(0.7rem, 0.75vw, 0.85rem)' }}>
              <ArrowRight size={12} /> Jump to on this page
            </p>
            <div className="flex flex-wrap gap-2.5">
              <a href="#why-patients-love-docsite" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-rose-50 border-2 border-rose-200 text-rose-800 font-semibold hover:bg-rose-600 hover:text-white hover:border-rose-600 transition-colors" style={{ fontSize: 'clamp(0.85rem, 0.95vw, 1.05rem)' }} data-testid="patients-nav-love">
                <Smile size={14} /> Why patients love it
              </a>
              <a href="#how-it-works-for-you" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-sky-50 border-2 border-sky-200 text-sky-800 font-semibold hover:bg-sky-600 hover:text-white hover:border-sky-600 transition-colors" style={{ fontSize: 'clamp(0.85rem, 0.95vw, 1.05rem)' }} data-testid="patients-nav-how">
                <Info size={14} /> How it works for you
              </a>
              <a href="#privacy-and-data" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-100 border-2 border-slate-200 text-slate-700 font-semibold hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors" style={{ fontSize: 'clamp(0.85rem, 0.95vw, 1.05rem)' }} data-testid="patients-nav-privacy">
                <Lock size={14} /> Privacy & your data
              </a>
              <Link to="/invite-your-doctor" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-50 border-2 border-emerald-200 text-emerald-800 font-semibold hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-colors" style={{ fontSize: 'clamp(0.85rem, 0.95vw, 1.05rem)' }} data-testid="patients-nav-invite">
                <UserPlus size={14} /> Invite your doctor
              </Link>
              <Link to="/dr/dr-rajesh-sharma" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-amber-50 border-2 border-amber-200 text-amber-800 font-semibold hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-colors" style={{ fontSize: 'clamp(0.85rem, 0.95vw, 1.05rem)' }} data-testid="patients-nav-sample">
                <Stethoscope size={14} /> Try a sample doctor
              </Link>
            </div>
          </nav>
        </div>
      </section>

      {/* Value pillars */}
      <section id="why-patients-love-docsite" className="bg-white border-y border-slate-200 py-20 md:py-28 scroll-mt-20" data-testid="patients-pillars-section">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mb-3">Why patients love DocSite</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Built around how people actually visit doctors in India — not how tech platforms wish they did.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUE_PILLARS.map(({ icon: Icon, title, desc, color }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-md hover:border-sky-200 transition-all" data-testid={`patients-pillar-${color}`}>
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 border ${colorMap[color]}`}>
                  <Icon size={20} strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-medium text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works-for-you" className="py-20 md:py-28 scroll-mt-20" data-testid="patients-how-it-works">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-sky-600 mb-3">How it works</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Three steps. No friction.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {HOW_IT_WORKS.map(({ num, icon: Icon, title, desc }) => (
              <div key={num} className="rounded-3xl bg-white border border-slate-200 p-7 relative overflow-hidden">
                <span className="absolute -top-4 -right-2 text-7xl font-bold text-slate-100 select-none" style={{ fontFamily: 'Outfit, sans-serif' }}>{num}</span>
                <Icon size={22} className="text-sky-600 mb-4 relative" strokeWidth={1.5} />
                <h3 className="text-lg font-medium text-slate-900 relative" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mt-2 relative">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy strip */}
      <section id="privacy-and-data" className="py-16 bg-slate-900 text-white scroll-mt-20" data-testid="patients-privacy-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold mb-5">
              <Lock size={12} /> YOUR DATA, YOUR TERMS
            </div>
            <h2 className="text-3xl tracking-tight font-medium leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              We don't track you. We don't sell your data. We don't spam you.
            </h2>
            <p className="text-sm text-slate-300 mt-4 leading-relaxed">
              DocSite is a platform for doctors to host their own websites. Patients aren't required to sign up to book. When you do provide info for a booking, it stays between you and your doctor.
            </p>
          </div>
          <div className="space-y-3">
            {[
              'No mandatory account, no password, no login',
              'IP addresses are SHA-256 hashed before storage',
              'No retargeting pixels, no ad networks, no social trackers',
              'You can download your data anytime from the Appointments page',
              'No one outside you and your doctor sees booking details',
            ].map(t => (
              <div key={t} className="flex items-start gap-3">
                <CheckCircle size={16} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-200 leading-relaxed">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 md:py-24 bg-gradient-to-br from-sky-50 via-white to-emerald-50/40" data-testid="patients-cta-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ask your doctor if they're on DocSite.
          </h2>
          <p className="text-base text-slate-600 mt-4 max-w-xl mx-auto">
            If they are, bookmark their page — it works offline and you'll never hunt for their number again. If not, nudge them. They'll thank you.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/invite-your-doctor" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors shadow-md shadow-emerald-500/20" data-testid="patients-cta-invite">
              <MessageCircle size={16} /> Send a WhatsApp invite <ArrowRight size={16} />
            </Link>
            <Link to="/dr/dr-rajesh-sharma" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-slate-200 text-slate-700 font-medium hover:border-slate-300 transition-colors" data-testid="patients-cta-sample">
              Try a sample doctor site <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-slate-200 text-slate-700 font-medium hover:border-slate-300 transition-colors" data-testid="patients-cta-contact">
              <Phone size={14} /> Talk to our team
            </Link>
          </div>
          <p className="text-xs text-slate-400 mt-5 italic">
            Pre-written message + a brochure link, sent from your own WhatsApp. Takes 3 seconds.
          </p>
        </div>
      </section>
    </div>
  );
}
